/* assets/littleg-profile.js — LITTLEG-PROFILE-LOCAL-IDENTITY-02
 * Device-local LittleG identity state owner.
 *
 * RESPONSIBILITY BOUNDARY (0 Conflict / 0 Overlap Contract)
 * - Map canvas, UI cards & markers: assets/littleg-kid-map.js
 * - Read-only UI state & marker palette: assets/littleg-kid-active-state.js (V1.12.0)
 * - Location privacy & coordinate transformation: assets/littleg-kid-location-privacy.js (V1.3.0)
 * - Social graph & record persistence: assets/littleg-family-data.js
 * - Shopify Customer account authentication: Shopify Customer Accounts / Liquid DOM
 * - Living CV career context is self-declared device-private data, not a second customer/account identity.\n * - No Shopify Customer metafield or cloud-CV write belongs here.\n * - THIS FILE OWNS EXCLUSIVELY:
 *   1. Device-local profile lifecycle (visitor -> littleg -> customer).
 *   2. Idempotent identity state reconciliation triggered by local activity events.
 *   3. Dispatching `littleg:profile-change` events without touching map DOM, coordinates, or backend APIs.
 */
(function littleGProfile(window, document) {
  'use strict';

  var VERSION = 'LITTLEG-PROFILE-LOCAL-IDENTITY-02';
  var STORAGE_KEY = 'littleg:profile:v1';
  var AVATARS = Object.freeze({ city: true, map: true, star: true, home: true });

  if (window.LittleGProfile && window.LittleGProfile.VERSION === VERSION) return;

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function safeLocal() {
    try {
      return window.localStorage || null;
    } catch (_) {
      return null;
    }
  }

  function now() {
    return new Date().toISOString();
  }

  function uid() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return 'lg-local-' + window.crypto.randomUUID();
      }
    } catch (_) {}
    return 'lg-local-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 9);
  }

  function locale() {
    return clean(document.documentElement && document.documentElement.lang).slice(0, 24) || 'zh-HK';
  }

  function customerConnected() {
    if (window.CUSTOMER_DATA && window.CUSTOMER_DATA.isLoggedIn === true) return true;
    return Boolean(document.querySelector('[data-littleg-profile-customer="true"]'));
  }

  function normalize(input) {
    if (!input || typeof input !== 'object') return null;
    var source = input;
    var id = clean(source.profile_id || source.id).slice(0, 120);
    if (!id) return null;

    var avatar = clean(source.avatar);
    if (!AVATARS[avatar]) avatar = 'city';

    return Object.freeze({
      version: 1,
      profile_id: id,
      display_name: clean(source.display_name || source.displayName).slice(0, 40),
      avatar: avatar,
      home_region: clean(source.home_region || source.homeRegion).slice(0, 80),
      industry: clean(source.industry).slice(0, 80),
      company: clean(source.company).slice(0, 100),
      career_role: clean(source.career_role).slice(0, 100),
      about: clean(source.about).slice(0, 500),
      locale: clean(source.locale).slice(0, 24) || locale(),
      storage_mode: 'local_private',
      public_profile: false,
      cloud_sync: false,
      created_at: clean(source.created_at) || now(),
      activated_at: clean(source.activated_at) || clean(source.created_at) || now(),
      activation_reason: clean(source.activation_reason).slice(0, 80) || 'manual',
      updated_at: clean(source.updated_at) || now()
    });
  }

  function read() {
    var store = safeLocal();
    if (!store) return null;
    try {
      return normalize(JSON.parse(store.getItem(STORAGE_KEY) || 'null'));
    } catch (_) {
      return null;
    }
  }

  function emit(reason, profile) {
    try {
      document.dispatchEvent(new CustomEvent('littleg:profile-change', {
        bubbles: true,
        detail: Object.freeze({
          reason: clean(reason || 'change'),
          identity_state: identityState(),
          profile: profile || null,
          customer_connected: customerConnected()
        })
      }));
    } catch (_) {}
  }

  function write(profile, reason) {
    var store = safeLocal();
    var safe = normalize(profile);
    if (!store || !safe) return null;

    var updated = normalize(Object.assign({}, safe, { updated_at: now() }));
    if (!updated) return null;

    try {
      store.setItem(STORAGE_KEY, JSON.stringify(updated));
      emit(reason || 'update', updated);
      return updated;
    } catch (_) {
      return null;
    }
  }

  function create(seed, reason) {
    var existing = read();
    if (existing) return existing;

    var source = seed && typeof seed === 'object' ? seed : {};
    return write({
      profile_id: uid(),
      display_name: clean(source.display_name || source.displayName),
      avatar: clean(source.avatar) || 'city',
      home_region: clean(source.home_region || source.homeRegion),
      locale: clean(source.locale) || locale(),
      created_at: now(),
      activated_at: now(),
      activation_reason: clean(reason || source.activation_reason || 'manual')
    }, reason || 'create');
  }

  function update(patch, reason) {
    var current = read() || create({}, reason || 'profile-settings');
    if (!current) return null;

    var source = patch && typeof patch === 'object' ? patch : {};
    // Keep existing legacy profile_id and timestamps; extend the same v1 record only.
    return write(Object.assign({}, current, {
      display_name: source.display_name != null ? clean(source.display_name) : current.display_name,
      avatar: source.avatar != null ? clean(source.avatar) : current.avatar,
      home_region: source.home_region != null ? clean(source.home_region) : current.home_region,
      industry: source.industry != null ? clean(source.industry) : current.industry,
      company: source.company != null ? clean(source.company) : current.company,
      career_role: source.career_role != null ? clean(source.career_role) : current.career_role,
      about: source.about != null ? clean(source.about) : current.about,
      locale: source.locale != null ? clean(source.locale) : current.locale
    }), reason || 'profile-settings');
  }

  function clear(reason) {
    var store = safeLocal();
    if (!store) return false;
    try {
      store.removeItem(STORAGE_KEY);
      emit(reason || 'clear', null);
      return true;
    } catch (_) {
      return false;
    }
  }

  function identityState() {
    if (customerConnected()) return 'customer';
    return read() ? 'littleg' : 'visitor';
  }

  function localActivityExists() {
    try {
      var results = window.LittleGResultStorage;
      if (results && typeof results.listAll === 'function' && results.listAll().length) return true;
    } catch (_) {}

    try {
      var family = window.LittleGFamilyData;
      if (family && typeof family.listRecords === 'function' && family.listRecords().length) return true;
      if (
        family &&
        family.socialGraph &&
        typeof family.socialGraph.listPlaces === 'function' &&
        family.socialGraph.listPlaces().length
      ) return true;
    } catch (_) {}

    return false;
  }

  function activate(reason) {
    return read() || create({}, reason || 'local-save');
  }

  function reconcile() {
    if (!read() && localActivityExists()) {
      activate('existing-local-activity');
    }
    return snapshot();
  }

  function snapshot() {
    var profile = read();
    return Object.freeze({
      version: VERSION,
      identity_state: identityState(),
      customer_connected: customerConnected(),
      storage_mode: profile ? 'local_private' : 'none',
      profile: profile
    });
  }


  // One local CV-file persistence authority: LittleGProfile, not Shopify, Core,
  // My Tools or a second session owner. Browser-managed IndexedDB is NOT encrypted.
  var RESUME_DB_NAME = 'littleg-profile-local-files-v1';
  var RESUME_STORE_NAME = 'resumes';
  var MAX_RESUME_FILE_BYTES = 5 * 1024 * 1024;
  var RESUME_SUBJECT_RE = /^littleg_[a-f0-9]{40}$/;

  function resumeSubject() {
    var session = window.LittleGWebSession && window.LittleGWebSession.getState
      ? window.LittleGWebSession.getState() : null;
    var subject = session && session.status === 'authenticated'
      ? clean(session.profile && session.profile.id) : '';
    if (!RESUME_SUBJECT_RE.test(subject)) throw new Error('resume_session_required');
    return subject;
  }

  function openResumeDb() {
    return new Promise(function (resolve, reject) {
      if (!window.indexedDB) { reject(new Error('resume_storage_unavailable')); return; }
      var request;
      try { request = window.indexedDB.open(RESUME_DB_NAME, 1); }
      catch (_) { reject(new Error('resume_storage_unavailable')); return; }
      request.onupgradeneeded = function () {
        if (!request.result.objectStoreNames.contains(RESUME_STORE_NAME)) {
          request.result.createObjectStore(RESUME_STORE_NAME, { keyPath: 'subject' });
        }
      };
      request.onsuccess = function () { resolve(request.result); };
      request.onerror = function () { reject(new Error('resume_storage_unavailable')); };
      request.onblocked = function () { reject(new Error('resume_storage_unavailable')); };
    });
  }

  async function withResumeStore(mode, operation) {
    var subject = resumeSubject();
    var database = await openResumeDb();
    // Recheck after the asynchronous DB open; an account switch invalidates the call.
    if (resumeSubject() !== subject) { database.close(); throw new Error('resume_session_changed'); }
    return new Promise(function (resolve, reject) {
      var transaction, request, result;
      try {
        transaction = database.transaction(RESUME_STORE_NAME, mode);
        request = operation(transaction.objectStore(RESUME_STORE_NAME), subject);
      } catch (_) { database.close(); reject(new Error('resume_storage_unavailable')); return; }
      request.onsuccess = function () { result = request.result; };
      transaction.oncomplete = function () {
        database.close();
        // Do not hand account A's Blob to account B if the session changed.
        try { if (resumeSubject() !== subject) throw new Error('resume_session_changed'); resolve(result); }
        catch (error) { reject(error); }
      };
      transaction.onabort = transaction.onerror = function () {
        database.close(); reject(new Error('resume_storage_unavailable'));
      };
    });
  }

  async function resumeSave(file) {
    var originalSubject = resumeSubject();
    if (!file || typeof file.name !== 'string' || typeof file.slice !== 'function' ||
        !Number.isSafeInteger(file.size) || file.size < 5 || file.size > MAX_RESUME_FILE_BYTES) {
      throw new Error('resume_invalid_file');
    }
    var name = clean(file.name).replace(/[\\/\x00-\x1f\x7f]/g, '').slice(0, 120);
    var pdf = /\.pdf$/i.test(name);
    var docx = /\.docx$/i.test(name);
    if (!pdf && !docx) throw new Error('resume_invalid_file');
    var bytes = new Uint8Array(await file.slice(0, 5).arrayBuffer());
    var validPdf = pdf && bytes[0] === 37 && bytes[1] === 80 &&
      bytes[2] === 68 && bytes[3] === 70 && bytes[4] === 45;
    var validDocx = docx && bytes[0] === 80 && bytes[1] === 75 &&
      (bytes[2] === 3 || bytes[2] === 5 || bytes[2] === 7);
    if (!validPdf && !validDocx) throw new Error('resume_invalid_file');
    var mime = pdf ? 'application/pdf' :
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    var blob = file.slice(0, file.size, mime);
    if (resumeSubject() !== originalSubject) throw new Error('resume_session_changed');
    var savedAt = new Date().toISOString();
    await withResumeStore('readwrite', function (store, subject) {
      if (subject !== originalSubject) throw new Error('resume_session_changed');
      return store.put({
        subject: subject, blob: blob, name: name, mime: mime,
        size: file.size, saved_at: savedAt
      });
    });
    return { name: name, size: file.size, saved_at: savedAt, storage_mode: 'local_private', cloud_sync: false };
  }

  async function resumeInfo() {
    var record = await withResumeStore('readonly', function (store, subject) { return store.get(subject); });
    return record ? {
      name: record.name, size: record.size, saved_at: record.saved_at,
      storage_mode: 'local_private', cloud_sync: false
    } : null;
  }

  async function resumeGet() {
    var record = await withResumeStore('readonly', function (store, subject) { return store.get(subject); });
    return record && record.blob && record.name
      ? { blob: record.blob, name: record.name, mime: record.mime } : null;
  }

  async function resumeRemove() {
    await withResumeStore('readwrite', function (store, subject) { return store.delete(subject); });
    return true;
  }

  // Cloud records are an owner-scoped cache, never the historical guest profile.
  // Loading or signing in does not import STORAGE_KEY or the CV-file database.
  var livingCV = null;
  function cloudSubject() {
    var session = window.LittleGWebSession && window.LittleGWebSession.getState();
    var subject = session && session.status === 'authenticated' && session.profile && session.profile.id;
    return /^littleg_[a-f0-9]{40}$/.test(subject || '') ? subject : '';
  }
  function visibleProfile() {
    var snapshot = livingCV && livingCV.snapshot();
    if (!snapshot || !snapshot.owner || !snapshot.authorized) return read();
    var records = snapshot.records.filter(function (entry) { return !entry.deleted_at; });
    var profile = records.find(function (entry) { return entry.record_type === 'profile'; });
    var career = records.find(function (entry) { return entry.record_type === 'career'; });
    return Object.freeze(Object.assign({}, normalize(Object.assign({ profile_id: snapshot.owner, created_at: now() },
      profile && profile.content, career && career.content)), { storage_mode: 'owner_cache', cloud_sync: true }));
  }
  async function updateSynchronized(patch, expected) {
    if (!livingCV || !livingCV.snapshot().authorized) throw new Error('private_sync_authorization_required');
    var subject = cloudSubject();
    var snapshot = livingCV.snapshot();
    var current = visibleProfile() || {};
    var source = Object.assign({}, current, patch);
    var savedTypes = [];
    for (var type of ['profile', 'career']) {
      try {
        var fields = type === 'profile' ? ['display_name', 'avatar', 'home_region', 'locale']
          : ['industry', 'company', 'career_role', 'about'];
        var content = {};
        fields.forEach(function (key) { if (source[key] != null) content[key] = clean(source[key]); });
        var id = await window.LittleGSyncEngine.singletonId(subject, type);
        if (cloudSubject() !== subject || snapshot.owner !== subject) throw new Error('session_changed');
        await livingCV.save(type, content, id, false, expected && typeof expected === 'object' ? expected[type] : undefined);
        savedTypes.push(type);
      } catch (error) {
        if (savedTypes.length) {
          error.partialSave = true;
          error.savedTypes = savedTypes.slice();
        }
        throw error;
      }
    }
    return visibleProfile();
  }
  async function importLocalProfile() {
    if (!livingCV) throw new Error('sync_unavailable');
    var subject = cloudSubject();
    // Never replace an existing cloud profile via a first-time legacy import.
    await livingCV.reconcile();
    if (!subject || cloudSubject() !== subject || livingCV.snapshot().owner !== subject) throw new Error('session_changed');
    if (livingCV.snapshot().records.some(function (entry) {
      return entry.record_type === 'profile' || entry.record_type === 'career';
    })) throw new Error('existing_cloud_profile_requires_resolution');
    var original = read();
    if (!original) throw new Error('no_local_profile');
    return updateSynchronized(original); // Original STORAGE_KEY remains recoverable.
  }
  if (window.LittleGSyncEngine && window.LittleGWebSession) {
    livingCV = window.LittleGSyncEngine.create({
      session: cloudSubject,
      request: function (url, options) { return window.LittleGWebSession.authenticatedFetch(url, options); },
      storage: {
        exclusive: function (subject, operation) {
          if (!window.navigator.locks) return Promise.reject(new Error('browser_lock_required'));
          return window.navigator.locks.request('littleg:cv:' + subject, operation);
        },
        read: async function (subject) {
          var store = safeLocal(); if (!store) throw new Error('local_storage_unavailable');
          return JSON.parse(store.getItem('littleg:cv-cache:v1:' + subject) || 'null');
        },
        write: async function (subject, value) {
          var store = safeLocal(); if (!store) throw new Error('local_storage_unavailable');
          store.setItem('littleg:cv-cache:v1:' + subject, JSON.stringify(value));
        }
      },
      changed: function () { document.dispatchEvent(new CustomEvent('littleg:cv-change')); }
    });
    var boundSubject = null;
    function bindCloudAccount() {
      var subject = cloudSubject();
      if (subject === boundSubject) return;
      boundSubject = subject;
      void livingCV.bind().catch(function () {});
    }
    document.addEventListener('littleg:web-session-change', bindCloudAccount);
    ['online', 'pageshow', 'focus'].forEach(function (name) {
      window.addEventListener(name, function () { void livingCV.reconcile().catch(function () {}); });
    });
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) void livingCV.reconcile().catch(function () {});
    });
    window.setInterval(function () {
      if (!document.hidden && cloudSubject()) void livingCV.reconcile().catch(function () {});
    }, 30000);
    bindCloudAccount();
  }
  window.LittleGProfile = Object.freeze({
    VERSION: VERSION,
    get storageMode() { return livingCV && livingCV.snapshot().authorized ? 'owner_cache' : 'local_private'; },
    publicProfile: false,
    get cloudSync() { return !!(livingCV && livingCV.snapshot().authorized); },
    get: visibleProfile,
    livingCV: livingCV,
    updateSynchronized: updateSynchronized,
    importLocalProfile: importLocalProfile,
    create: create,
    update: update,
    activate: activate,
    clear: clear,
    getIdentityState: identityState,
    reconcile: reconcile,
    snapshot: snapshot,
    resumeSave: resumeSave,
    resumeInfo: resumeInfo,
    resumeGet: resumeGet,
    resumeRemove: resumeRemove
  });

  // Event listeners strictly for identity state reconciliation
  [
    'littleg:family-record-saved',
    'littleg:city-mission:result-saved',
    'littleg:family-social-graph-change',
    'littleg:child-profile-change'
  ].forEach(function (eventName) {
    document.addEventListener(eventName, function () {
      activate(eventName);
    });
  });

  window.addEventListener('pageshow', reconcile);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile, { once: true });
  } else {
    reconcile();
  }
})(window, document);
